import React, { useState } from 'react';
import { Send, ChefHat, Wine, X, AlertTriangle, Check, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * QuickMessageModal - Envoi rapide de message depuis le POS
 * Permet d'envoyer un message pré-rempli à la cuisine ou au bar
 */
const QuickMessageModal = ({
    isOpen,
    onClose,
    tableNumber,
    api
}) => {
    const [recipient, setRecipient] = useState('cuisine');
    const [messageType, setMessageType] = useState('ready');
    const [customMessage, setCustomMessage] = useState('');
    const [sending, setSending] = useState(false);

    const MESSAGE_TYPES = [
        { value: 'ready', label: 'Commande prête', icon: Check, color: 'emerald' },
        { value: 'problem', label: 'Problème', icon: AlertTriangle, color: 'amber' },
        { value: 'urgent', label: 'URGENT', icon: AlertTriangle, color: 'red' }
    ];

    const RECIPIENTS = [
        { value: 'cuisine', label: 'Cuisine', icon: ChefHat },
        { value: 'bar', label: 'Bar', icon: Wine }
    ];

    const handleSend = async () => {
        setSending(true);
        try {
            const token = localStorage.getItem('token');
            
            // 1. Envoyer au team-chat (existant)
            const response = await fetch(`${API_BASE}/api/team-chat/quick-message?recipient=${recipient}&table_number=${tableNumber}&message_type=${messageType}${customMessage ? `&custom_message=${encodeURIComponent(customMessage)}` : ''}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) throw new Error('Failed to send to team-chat');
            
            // 2. Envoyer également à l'écran cuisine directement (nouveau système)
            // Cela permet d'afficher le message même sans commande active
            if (recipient === 'cuisine') {
                const messageText = customMessage 
                    ? `${MESSAGE_TYPES.find(t => t.value === messageType)?.label}: ${customMessage}`
                    : MESSAGE_TYPES.find(t => t.value === messageType)?.label || 'Message';
                
                await fetch(`${API_BASE}/api/kitchen/messages`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        message: messageText,
                        table_number: String(tableNumber),
                        priority: messageType === 'urgent' ? 'urgent' : 'normal'
                    })
                }).catch(err => console.warn('Kitchen message backup failed:', err));
            }
            
            toast.success(`Message envoyé à ${recipient === 'cuisine' ? 'la cuisine' : 'le bar'}`);
            onClose();
            setCustomMessage('');
            setMessageType('ready');
            
        } catch (error) {
            console.error('Error sending quick message:', error);
            toast.error('Erreur lors de l\'envoi');
        } finally {
            setSending(false);
        }
    };

    // Preview message
    const previewMessage = `Table ${tableNumber} - ${MESSAGE_TYPES.find(t => t.value === messageType)?.label}${customMessage ? `: ${customMessage}` : ''}`;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid="quick-message-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-emerald-600" />
                        Message rapide - Table {tableNumber}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Recipient Selection */}
                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-2 block">
                            Envoyer à:
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            {RECIPIENTS.map(r => (
                                <button
                                    key={r.value}
                                    onClick={() => setRecipient(r.value)}
                                    className={cn(
                                        "flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all",
                                        recipient === r.value
                                            ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30"
                                            : "border-gray-200 dark:border-zinc-700 hover:border-gray-300"
                                    )}
                                >
                                    <r.icon className={cn(
                                        "w-5 h-5",
                                        recipient === r.value ? "text-emerald-600" : "text-gray-500"
                                    )} />
                                    <span className={cn(
                                        "font-medium",
                                        recipient === r.value ? "text-emerald-700" : "text-gray-700"
                                    )}>
                                        {r.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Message Type */}
                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-2 block">
                            Type de message:
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            {MESSAGE_TYPES.map(t => (
                                <button
                                    key={t.value}
                                    onClick={() => setMessageType(t.value)}
                                    className={cn(
                                        "flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all",
                                        messageType === t.value
                                            ? t.color === 'emerald' 
                                                ? "border-emerald-500 bg-emerald-50"
                                                : t.color === 'amber'
                                                    ? "border-amber-500 bg-amber-50"
                                                    : "border-red-500 bg-red-50"
                                            : "border-gray-200 hover:border-gray-300"
                                    )}
                                >
                                    <t.icon className={cn(
                                        "w-5 h-5",
                                        messageType === t.value
                                            ? t.color === 'emerald' 
                                                ? "text-emerald-600"
                                                : t.color === 'amber'
                                                    ? "text-amber-600"
                                                    : "text-red-600"
                                            : "text-gray-500"
                                    )} />
                                    <span className={cn(
                                        "text-xs font-medium",
                                        messageType === t.value && t.color === 'red' && "text-red-600 font-bold"
                                    )}>
                                        {t.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Custom Message */}
                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-2 block">
                            Message personnalisé (optionnel):
                        </label>
                        <Textarea
                            value={customMessage}
                            onChange={(e) => setCustomMessage(e.target.value)}
                            placeholder="Ex: Sans piment, supplément frites..."
                            rows={2}
                        />
                    </div>

                    {/* Preview */}
                    <div className="p-3 bg-gray-100 dark:bg-zinc-800 rounded-xl">
                        <p className="text-xs text-gray-500 mb-1">Aperçu:</p>
                        <p className={cn(
                            "font-medium",
                            messageType === 'urgent' && "text-red-600",
                            messageType === 'problem' && "text-amber-600",
                            messageType === 'ready' && "text-emerald-600"
                        )}>
                            {previewMessage}
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Annuler
                    </Button>
                    <Button 
                        onClick={handleSend}
                        disabled={sending}
                        className={cn(
                            messageType === 'urgent' && "bg-red-500 hover:bg-red-600",
                            messageType === 'problem' && "bg-amber-500 hover:bg-amber-600",
                            messageType === 'ready' && "bg-emerald-500 hover:bg-emerald-600"
                        )}
                    >
                        {sending ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <>
                                <Send className="w-4 h-4 mr-2" />
                                Envoyer
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default QuickMessageModal;
